'use client';

import { Fragment, useMemo } from 'react';
import { ReactComponent as Bars } from '@assets/icons/bars.svg';
import { ReactComponent as Check } from '@assets/icons/check.svg';
import DefaultUserAvatar from '@assets/icons/default-user-avatar.svg';
import { ReactComponent as Times } from '@assets/icons/times.svg';
import config from 'config';
import { ROUTES } from 'constant';
import { usePathname, useRouter } from 'next/navigation';

import {
  Box,
  Button,
  Collapse,
  Flex,
  Text,
  useDisclosure,
  useTheme
} from '@chakra-ui/react';

import { useAppSelector } from 'store/hooks';
import { userSelector } from 'store/reducers/userSlice';

import { TabType } from 'types';

import { Avatar } from 'components/Avatar';
import { Divider } from 'ui/Divider';

const {
  dashboard: {
    tabs,
    mobileTabs,
    sidebar: { isReferFriend }
  }
} = config;

const menuButtonStaticStyles = {
  position: 'relative',
  borderRadius: 0,
  opacity: 1,
  bg: 'transparent',
  p: 0,
  minW: 'none',
  ml: '55px',

  '& > span': {
    margin: 0
  },

  '&:hover, &:active, &[data-active]': {
    opacity: 1
  },

  '&::before': {
    content: '""',
    position: 'absolute',
    display: 'block',
    width: '80px',
    height: '80px',
    top: '50%',
    left: '50%',
    transform: 'translate(-70%, -50%)'
  }
};

const labelStyles = {
  textTransform: 'uppercase',
  fontSize: '16px',
  lineHeight: '120%',
  mx: '5px',
  textAlign: 'center'
};

const menuListStaticStyles = {
  padding: '0px 15px',
  border: 0,
  borderRadius: 0,
  transition: 'background 0s, opacity 0s'
};

const menuItemStaticStyles = {
  textTransform: 'uppercase',
  padding: '20px 15px',
  w: '100%',
  justifyContent: 'space-between',
  fontSize: '13px',
  lineHeight: '18px',
  cursor: 'pointer',

  '> span': {
    margin: 0
  }
};

const menuDividerStaticStyles = {
  opacity: 1,
  margin: 0,

  '&:last-of-type': {
    display: 'none'
  }
};

const Menu = () => {
  const router = useRouter();
  const {
    colors: {
      palette: {
        components: {
          ui: { menu: palette }
        }
      }
    }
  } = useTheme();
  const pathname = usePathname();
  const { isOpen, onToggle } = useDisclosure();
  const { user } = useAppSelector(userSelector);

  const tabsData = useMemo(() => {
    const arr = [...(tabs ?? []), ...(mobileTabs ?? [])];

    if (isReferFriend) {
      arr.push({
        label: 'REFER A FRIEND',
        link: ROUTES.referFriend
      });
    }

    return arr;
  }, []);

  const getLabel = () => {
    const item = tabsData.find(({ link }: TabType) => link === pathname);
    return item?.label || tabsData[0].label;
  };

  const menuListStyles = {
    ...menuListStaticStyles,
    background: palette.menuListBackground
  };

  const menuItemStyles = {
    ...menuItemStaticStyles,
    background: palette.menuItemBackground,

    '&.active-link': {
      fontWeight: palette.menuItemActiveFontWeight
    }
  };

  const menuDividerStyles = {
    ...menuDividerStaticStyles,
    borderColor: palette.menuDividerBorderColor
  };

  const onMenuClick = (link: string) => {
    onToggle();
    router.push(link);
  };

  return (
    <Flex
      position='relative'
      alignItems='center'
      justifyContent='space-between'
      px='20px'
      py='12px'
      background={palette.menuBackground}
    >
      <Avatar
        key={`${user?.givname} ${user?.surname}`}
        src={user?.photo || DefaultUserAvatar}
        w={{ base: '40px', md: '60px' }}
        h={{ base: '40px', md: '60px' }}
        flexShrink={0}
        alt={`${user?.givname} ${user?.surname}`}
        borderRadius='50%'
        borderWidth={3}
        borderColor={palette.menuAvatarBorderColor}
        useBadges={false}
        isMember={!!user?.is_member}
        isVIP={user?.is_vip === 1}
        isInfluencer={user?.is_influencer === 1}
      />
      <Text color={palette.menuLabelColor} sx={labelStyles}>
        {getLabel()}
      </Text>
      <Button
        onClick={onToggle}
        rightIcon={
          isOpen ? (
            <Times width='20px' height='20px' fill={palette.menuIconColor} />
          ) : (
            <Bars width='20px' height='20px' fill={palette.menuIconColor} />
          )
        }
        sx={menuButtonStaticStyles}
      />
      <Collapse
        in={isOpen}
        style={{
          position: 'absolute',
          width: '100%',
          zIndex: 10,
          top: '100%',
          left: 0
        }}
      >
        <Box sx={menuListStyles}>
          {tabsData.map(({ link, label }: TabType) => {
            const isActive = pathname === link;

            return (
              <Fragment key={label}>
                <Flex
                  onClick={() => onMenuClick(link)}
                  sx={menuItemStyles}
                  className={isActive ? 'active-link' : ''}
                >
                  {label}
                  <Box>
                    {isActive ? (
                      <Check
                        fill={palette.menuIconColor}
                        width='20px'
                        height='20px'
                      />
                    ) : undefined}
                  </Box>
                </Flex>
                <Divider sx={menuDividerStyles} />
              </Fragment>
            );
          })}
        </Box>
      </Collapse>
    </Flex>
  );
};

export default Menu;
